import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Wind, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Cases heavy enough to earn an optional breathing pause before the debrief.
export const HEAVY_CASES = new Set([
  "hotline",
  "full-shelter",
  "shelter-kids",
  "shelter-ptsd",
  "shelter-dysregulation",
  "just-in-passing",
]);

const PAUSE_SECONDS = 20;

export const GroundingPause = ({ onContinue }) => {
  const [secondsLeft, setSecondsLeft] = useState(PAUSE_SECONDS);
  const [phase, setPhase] = useState("in");

  useEffect(() => {
    const tick = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    const breath = setInterval(() => setPhase((p) => (p === "in" ? "out" : "in")), 4000);
    return () => {
      clearInterval(tick);
      clearInterval(breath);
    };
  }, []);

  const done = secondsLeft === 0;

  return (
    <div data-testid="grounding-pause" className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-xl rounded-2xl border border-stone-200 bg-white p-8 sm:p-10 shadow-sm text-center"
      >
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
          <Wind className="h-4 w-4" />
          Take a moment
        </div>
        <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-semibold text-stone-900">
          That was a heavy case.
        </h2>
        <p className="mt-2 text-base leading-relaxed text-stone-600 max-w-md mx-auto">
          Before the debrief, you're invited to pause and take a few slow breaths.
          The same care you offer survivors belongs to you too.
        </p>

        <div className="mt-8 flex items-center justify-center">
          <motion.div
            animate={{ scale: [1, 1.35, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="h-32 w-32 rounded-full bg-emerald-100 flex items-center justify-center"
          >
            <div className="h-20 w-20 rounded-full bg-emerald-200/80 flex items-center justify-center">
              <span data-testid="breath-prompt" className="text-sm font-medium text-emerald-900">
                {phase === "in" ? "Breathe in" : "Breathe out"}
              </span>
            </div>
          </motion.div>
        </div>

        <p className="mt-6 text-sm text-stone-400" aria-live="polite">
          {done ? "Whenever you're ready." : `${secondsLeft} seconds`}
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {done ? (
            <Button
              data-testid="grounding-continue"
              onClick={onContinue}
              className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6"
            >
              Continue to your debrief <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          ) : (
            <button
              data-testid="grounding-skip"
              onClick={onContinue}
              className="text-sm font-medium text-stone-500 hover:text-emerald-800 underline underline-offset-4 transition-colors duration-200"
            >
              Skip ahead, I'm ready
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};
